import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  NonIndexRouteObject,
  IndexRouteObject,
} from 'react-router-dom';
import Home from '../pages/Home';
import SignInInfoTrac from '../infoTrac/pages/auth/signIn';
import SignInGuidance from '../Guidance/pages/auth/singleSignIn';
import DirectSignInGuidance from '../Guidance/pages/auth/directSignOn';
import DualAuthCheckInfoTrac from '../infoTrac/pages/auth/dualAuthCheck';
import { routes as menuRoutes } from '../components/Layout/Menu';
import { routes as menuInfoTracRoutes } from '../infoTrac/components/Layout/Menu';
import { guidanceRoutes as menuGuidanceRoutes } from '../Guidance/components/Layout/Menu';
import PrivateRoute from './PrivateRoute';
import { AuthUser as User } from '../store';
import { AnyObject } from '../types';
import { financialInstitutionRoutes } from '../Guidance/components/Layout/FinancialInstitution/Header';
import Notifications from '../Guidance/pages/notifications';
import NotificationEmail from '../Guidance/pages/notifications/email';

export interface RouteElements {
  [role: string]: React.ReactNode | null;
}

interface RouteObjectExtension {
  elements?: RouteElements;
  params?: ((user: User) => string) | AnyObject;
}

interface MyIndexRouteObject extends IndexRouteObject, RouteObjectExtension {}

interface MyNonIndexRouteObject extends NonIndexRouteObject, RouteObjectExtension {}

export type RouteObject = MyIndexRouteObject | MyNonIndexRouteObject;
export type RouteRoles = (string | ((user: User) => boolean))[];

export interface Route {
  route?: RouteObject;
  roles?: RouteRoles;
  name?: string; // string | ((user: User) => string)
  icon?: React.ReactNode; // React.ReactNode | ((user: User) => React.ReactNode)
  parent?: Route;
  hidden?: boolean;
  onClick?: () => void;
  children?: Route[];
  outlet?: true;
  roleNeeded?: 'user' | 'manager' | 'admin';
  privateRoute?: boolean;
  disabled?: string | ((user: User) => boolean);
  order?: number; // Items with bigger value will be first.

  // eslint-disable-next-line
  [key: string]: any;
}

export function addChildrenRoutes(routes: Route[]): Route[] {
  const list: Route[] = [];

  const recursiveUpdate = (item: Route, parent?: Route): Route => {
    if (parent) {
      // eslint-disable-next-line no-param-reassign
      item.parent = parent;

      if (parent?.route?.children && item.route) {
        parent.route.children.push(item.route);
      }

      if (!item.outlet && item.route) {
        list.push(item);

        if (parent.route) {
          const childList = (parent.route?.children || []).filter(child => child !== item.route);

          if (childList.length) {
            Object.assign(parent.route, {
              children: childList,
            });
          } else {
            // eslint-disable-next-line no-param-reassign
            delete parent.route.children;
          }
        }

        Object.assign(item.route, {
          path: `${parent.route?.path}/${item.route?.path}`.replace(/\/+/, '/').replace(/^\/+/, ''),
        });
      }
    }

    if (item.route) {
      const { route } = item;

      if (item.privateRoute && (route?.element || route?.elements)) {
        route.element = <PrivateRoute element={route?.element} elements={route?.elements} item={item} />;
      }

      if (item.children) {
        // eslint-disable-next-line no-param-reassign
        route.children = [];

        item.children.forEach(childRoute => recursiveUpdate(childRoute, item));
      }
    } else if (item.children) {
      item.children.filter(({ route }) => route).forEach(child => list.push(recursiveUpdate(child)));
    }

    return item;
  };

  return [...routes.map(route => recursiveUpdate(route)), ...list].filter(({ route }) => route);
}

export const routes: Route[] = addChildrenRoutes([
  {
    name: 'Home',
    route: {
      path: '/',
      element: <Home />,
    },
    children: [
      {
        name: 'Sign in',
        route: {
          path: '/infoTrac/sign-in',
          element: <SignInInfoTrac />,
        },
      },
      {
        name: 'Dual Authentication Check',
        route: {
          path: '/infoTrac/dual-authentication',
          element: <DualAuthCheckInfoTrac />,
        },
      },
      {
        name: 'Guidance: Single Sign On',
        route: {
          path: '/guidance/single-sign-on',
          element: <SignInGuidance />,
        },
      },
      {
        name: 'Guidance: Direct Sign On',
        route: {
          path: '/guidance/direct-sign-on',
          element: <DirectSignInGuidance />,
        },
      },
      {
        name: 'Guidance: Notifications',
        route: {
          path: '/guidance/notifications',
          element: <Notifications />,
        },
      },
      {
        name: 'Guidance: Notification Email',
        hidden: true,
        route: {
          path: '/guidance/notifications/:id',
          element: <NotificationEmail />,
        },
      },
      ...menuRoutes,
      ...menuInfoTracRoutes,
      ...menuGuidanceRoutes,
      ...financialInstitutionRoutes,
    ],
  },
]);

const router = createBrowserRouter([
  ...routes.map(({ route }) => route as RouteObject),
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

export function getRouteParams(path: string, user?: User | null): AnyObject {
  let params: ((user: User) => string) | AnyObject = {};

  routes.find(({ route }) => {
    if (route && route.path && route.path === path) {
      if (route.params) {
        params = route.params;
      }

      return true;
    }

    return false;
  });

  if (typeof params === 'function') {
    if (user) {
      return params(user);
    }

    return {};
  }

  return params;
}

export default function Routes(): React.ReactElement {
  return <RouterProvider router={router} />;
}
